import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from '@/router/routerCompat';
import { useMaintenanceStatusQuery } from '../hooks/queries/useCoreCatalogQueries';

const COOLDOWN_KEY = 'maintenance_check_cooldown';
const COOLDOWN_MS = 30000;

function isMaintenanceCheckBypass(pathname) {
  return pathname === '/maintenance' || pathname.startsWith('/auth');
}

function readCooldownActive() {
  try {
    const lastFail = sessionStorage.getItem(COOLDOWN_KEY);
    if (lastFail && Date.now() - parseInt(lastFail, 10) < COOLDOWN_MS) {
      return true;
    }
  } catch (_) {
    /* ignore sessionStorage */
  }
  return false;
}

function setCooldown() {
  try {
    sessionStorage.setItem(COOLDOWN_KEY, String(Date.now()));
  } catch (_) {
    /* ignore sessionStorage */
  }
}

/**
 * Redirects to `/maintenance` when API reports maintenance_mode.
 * Replaces ad-hoc fetch in AppLayout (Sprint C2).
 */
export function MaintenanceRouteGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const shouldBypass = isMaintenanceCheckBypass(location.pathname);
  const [cooldownActive, setCooldownActive] = useState(() =>
    shouldBypass ? false : readCooldownActive()
  );

  useEffect(() => {
    if (shouldBypass) {
      setCooldownActive(false);
      return;
    }
    setCooldownActive(readCooldownActive());
  }, [location.pathname, shouldBypass]);

  const maintenanceQuery = useMaintenanceStatusQuery({
    enabled: !shouldBypass && !cooldownActive,
  });

  useEffect(() => {
    if (shouldBypass || cooldownActive) return;
    if (maintenanceQuery.isSuccess && maintenanceQuery.data?.maintenance_mode) {
      navigate('/maintenance', { replace: true });
    }
  }, [
    shouldBypass,
    cooldownActive,
    maintenanceQuery.isSuccess,
    maintenanceQuery.data,
    navigate,
  ]);

  useEffect(() => {
    if (shouldBypass || cooldownActive) return;
    if (!maintenanceQuery.isError) return;
    setCooldown();
    setCooldownActive(true);
  }, [shouldBypass, cooldownActive, maintenanceQuery.isError]);

  useEffect(() => {
    const handler = () => {
      if (location.pathname !== '/maintenance') {
        navigate('/maintenance', { replace: true });
      }
    };
    window.addEventListener('navigateToMaintenance', handler);
    return () => window.removeEventListener('navigateToMaintenance', handler);
  }, [navigate, location.pathname]);

  return null;
}
